<template>
  <div
    class="flex min-h-0 w-full flex-col px-4 pb-0! pt-[calc(env(safe-area-inset-top)+62px)] tablet-h:p-[14px] tablet-h:pl-10 tablet-h:pr-[64px] desktop:pr-[26px]"
    @click="handleClickOutside"
  >
    <div class="mb-[15px] tablet-h:mb-4">
      <h1
        class="text-[24px] font-medium leading-[30px] text-gray-800 tablet-h:hidden"
      >
        {{ headerText }}
      </h1>
      <h1
        class="hidden text-[18px] font-medium leading-[20px] tracking-[-0.25px] text-gray-800 tablet-h:block"
      >
        {{ headerText }}
      </h1>
    </div>

    <FileSearchTable
      ref="tableRef"
      :items="items"
      :loading="loading"
      v-model:selected="selected"
      v-model:sort="sort"
      @scroll-end="fetchNextPage"
    />
  </div>

  <FileSearchSidebar
    :selected-item="selectedItem"
    :total="result?.searchItems.total"
    class="hidden h-[calc(100vh-61px)]! top-0 right-0 bottom-0 tablet-h:absolute tablet-h:flex desktop:static"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { type TableSort } from '@dacr/library'
import { formatSearchTotal } from '@/features/search/formatSearchTotal'
import {
  SearchSort,
  useSearchItemsQuery,
  type Item,
} from '@/shared/graphql/legacy/generated'
import FileSearchTable from '@/features/search/components/desktop/FileSearchTable.vue'
import FileSearchSidebar from '@/features/search/components/desktop/FileSearchSidebar.vue'

const route = useRoute()

const query = computed(() => (route.query.query as string) ?? '')

useHead({
  title: computed(
    () => `Search results in your files for "${query.value}" | Dacr`
  ),
})

const tableRef = ref<InstanceType<typeof FileSearchTable>>()

const selected = ref<string[]>([])
const sort = ref<TableSort | null>({ field: 'name', direction: 'asc' })
const sortBy = ref<SearchSort>(SearchSort.NameAsc)

const SORT_MAP: Record<string, SearchSort> = {
  'name:asc': SearchSort.NameAsc,
  'name:desc': SearchSort.NameDesc,
  'timestamp:asc': SearchSort.TimestampAsc,
  'timestamp:desc': SearchSort.TimestampDesc,
}

watch(sort, (value) => {
  if (!value) return
  const next = SORT_MAP[`${value.field}:${value.direction}`]
  if (next) sortBy.value = next
})

const variables = computed(() => ({
  query: query.value,
  sortBy: sortBy.value,
}))

const { result, loading, fetchMore } = useSearchItemsQuery(variables)

const items = computed(
  () => result.value?.searchItems.files as Item[] | undefined
)

const total = computed(() => result.value?.searchItems.total)

const headerText = computed(() =>
  total.value === undefined
    ? 'Searching…'
    : `${formatSearchTotal(total.value)} ${total.value === 1 ? 'result' : 'results'} for "${query.value}"`
)

const selectedItem = computed(() =>
  items.value?.find((item) => item.id === selected.value[0])
)

const fetchNextPage = () => {
  const next = result.value?.searchItems.next
  if (!next || loading.value) return

  fetchMore({
    variables: { next },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!newData) return previousData
      return {
        __typename: 'Query' as const,
        searchItems: {
          ...previousData.searchItems,
          files: [
            ...previousData.searchItems.files,
            ...newData.searchItems.files,
          ],
          next: newData.searchItems.next,
          total: newData.searchItems.total,
        },
      }
    },
  })
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const tableEl = tableRef.value?.$el
  if (tableEl && !tableEl.contains(target)) {
    selected.value = []
  }
}

onBeforeUnmount(() => {
  selected.value = []
})
</script>
